import { t } from 'i18next';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';

const VisitDetails = ({ visit }) => {
	const [visitInfo, setVisitInfo] = useState({
		height: '', weight: '', pulse: '', bp: '', spo2: '', temperature: '',
		complaints: [], history: '', diagnosis: [], prescriptions: [], advices: [],
		testRequireds: [], dietExercises: [],
	});

	useEffect(() => { if (visit) setVisitInfo(visit); }, [visit]);

	const validVitals = useMemo(() => {
		return visitInfo?.height || visitInfo?.weight || visitInfo?.pulse || visitInfo?.bp || visitInfo?.spo2 || visitInfo?.temperature;
	}, [visitInfo]);

	const validComplaints = useMemo(() =>
		visitInfo?.complaints?.filter(c =>
			c?.complaint?.name || c?.frequency?.name || c?.severity?.name || c?.duration?.name || c?.date
		) || [], [visitInfo]);

	const validDiagnosis = useMemo(() =>
		visitInfo?.diagnosis?.filter(d => d?.diagnosis?.name || d?.duration?.name || d?.date) || [], [visitInfo]);

	const validPrescriptions = useMemo(() =>
		visitInfo?.prescriptions?.filter(p =>
			p?.medicine?.name || p?.dose?.name || p?.timing?.name || p?.frequency?.name || p?.duration?.name || p?.notes
		) || [], [visitInfo]);

	const isAllEmpty = useMemo(() => {
		return !validVitals &&
			validComplaints.length === 0 &&
			!visitInfo.history &&
			validDiagnosis.length === 0 &&
			validPrescriptions.length === 0 &&
			visitInfo.advices?.length === 0 &&
			visitInfo.testRequireds?.length === 0 &&
			visitInfo.dietExercises?.length === 0;
	}, [validVitals, validComplaints, visitInfo, validDiagnosis, validPrescriptions]);

	const labelValue = (label, value) => (
		<div><strong>{label}:</strong> {value || 'N/A'}</div>
	);

	if (isAllEmpty) {
		return (
			<div className="d-flex justify-content-center align-items-center p-5">
				<strong>{t('VISIT.NO_DATA') || 'No data available'}</strong>
			</div>
		);
	}

	return (
		<div className="d-flex flex-column gap-3 p-3">

			{/* Vitals */}
			{validVitals && (
				<Card>
					<Card.Header>{t('VISIT.VITALS.TITLE')}</Card.Header>
					<Card.Body>
						<Row>
							<Col md={4}>{labelValue(t('VISIT.VITALS.HEIGHT'), Number(visitInfo?.height) ? Number(visitInfo?.height) + ' cm' : 'N/A')}</Col>
							<Col md={4}>{labelValue(t('VISIT.VITALS.WEIGHT'), Number(visitInfo?.weight) ? Number(visitInfo?.weight) + ' kg' : 'N/A')}</Col>
							<Col md={4}>{labelValue(t('VISIT.VITALS.PULSE'), Number(visitInfo?.pulse) ? Number(visitInfo?.pulse) + ' bpm' : 'N/A')}</Col>
							<Col md={4}>{labelValue(t('VISIT.VITALS.BP'), visitInfo?.bp ? visitInfo?.bp + ' mmHg' : 'N/A')}</Col>
							<Col md={4}>{labelValue(t('VISIT.VITALS.SPO2'), Number(visitInfo?.spo2) ? Number(visitInfo?.spo2) + '%' : 'N/A')}</Col>
							<Col md={4}>{labelValue(t('VISIT.VITALS.TEMPERATURE'), Number(visitInfo?.temperature) ? Number(visitInfo?.temperature) + ' F' : 'N/A')}</Col>
						</Row>
					</Card.Body>
				</Card>
			)}

			{/* Complaints */}
			{validComplaints.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.CHIEF_COMPONENTS_TITLE')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{validComplaints.map((c, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {c?.complaint?.name || 'N/A'}</strong>
								<div className="mt-1" style={{ marginLeft: '15px' }}>
									{c?.frequency?.name && <div><strong>{t('VISIT.FREQUENCY')}:</strong> {c.frequency.name}</div>}
									{c?.severity?.name && <div><strong>{t('VISIT.SEVERITY')}:</strong> {c.severity.name}</div>}
									{c?.duration?.name && <div><strong>{t('VISIT.DURATION')}:</strong> {c.duration.name}</div>}
									{c?.date && <div><strong>{t('VISIT.DATE')}:</strong> {moment(c.date).format('DD-MM-YYYY')}</div>}
								</div>
							</div>
						))}
					</Card.Body>
				</Card>
			)}

			{/* History */}
			{visitInfo.history && (
				<Card>
					<Card.Header>{t('VISIT.VISIT_HISTORY_TITLE')}</Card.Header>
					<Card.Body>
						<div style={{ whiteSpace: 'pre-wrap' }}>{visitInfo.history}</div>
					</Card.Body>
				</Card>
			)}

			{/* Diagnosis */}
			{validDiagnosis.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.DIAGNOSIS_TITLE')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{validDiagnosis.map((d, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {d?.diagnosis?.name || 'N/A'}</strong>
								<div className="mt-1" style={{ marginLeft: '15px' }}>
									{d?.duration?.name && <div><strong>{t('VISIT.DURATION')}:</strong> {d.duration.name}</div>}
									{d?.date && <div><strong>{t('VISIT.DATE')}:</strong> {moment(d.date).format('DD-MM-YYYY')}</div>}
								</div>
							</div>
						))}
					</Card.Body>
				</Card>
			)}

			{/* Prescriptions */}
			{validPrescriptions.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.RX')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{validPrescriptions.map((p, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {p?.medicine?.name || 'N/A'}</strong>
								<div className="mt-1" style={{ marginLeft: '15px' }}>
									{p?.dose?.name && <div><strong>{t('VISIT.DOSE')}:</strong> {p.dose.name}</div>}
									{p?.timing?.name && <div><strong>{t('VISIT.TIMING')}:</strong> {p.timing.name}</div>}
									{p?.frequency?.name && <div><strong>{t('VISIT.FREQUENCY')}:</strong> {p.frequency.name}</div>}
									{p?.duration?.name && <div><strong>{t('VISIT.DURATION')}:</strong> {p.duration.name}</div>}
									{p?.notes && <div><strong>{t('VISIT.NOTES_INSTRUCTIONS')}:</strong> {p.notes}</div>}
								</div>
							</div>
						))}
					</Card.Body>
				</Card>
			)}

			{/* Advices */}
			{visitInfo.advices?.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.ADVICE')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{visitInfo.advices.map((a, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {a?.advice?.name}:</strong> {a?.advice?.advice}
							</div>
						))}
					</Card.Body>
				</Card>
			)}

			{/* Tests */}
			{visitInfo.testRequireds?.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.TEST_REQUIRED')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{visitInfo.testRequireds.map((t, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {t?.test?.name}</strong>
							</div>
						))}
					</Card.Body>
				</Card>
			)}

			{/* Diet & Exercise */}
			{visitInfo.dietExercises?.length > 0 && (
				<Card>
					<Card.Header>{t('VISIT.DIET_AND_EXERCISE')}</Card.Header>
					<Card.Body style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '5px' }}>
						{visitInfo.dietExercises.map((d, index) => (
							<div key={index} className="mb-2">
								<strong>{index + 1}. {d?.dietExercise?.name}:</strong> {d?.dietExercise?.advice}
							</div>
						))}
					</Card.Body>
				</Card>
			)}

		</div>
	);
};

export default VisitDetails;
